import { createContext, useCallback, useContext, useState, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { JamesThread } from '../api/james';
import {
  sendMessage,
  quickChat,
  type JamesToolUseStartEvent,
  type JamesToolUseResultEvent,
  type JamesAskUserEvent,
  type StreamMessageOptions,
} from '../api/james';
// Streaming types — defined here (source of truth), re-exported from useJames.ts
export interface ClarifyingQuestion {
  id: string;
  question: string;
  options: Array<{ label: string; description?: string }>;
}

export interface StreamingState {
  isStreaming: boolean;
  currentText: string;
  activeTools: Map<string, { name: string; status: 'running' | 'completed' | 'error'; result?: unknown }>;
  error: string | null;
  pendingUserMessage: string | null;
  clarifyingQuestion: ClarifyingQuestion | null;
  wasInterrupted: boolean;
}

// Split into three contexts: panel state, thread state, and stream state
// so that toggling the panel doesn't re-render the chat tree, and vice versa.

interface JamesPanelContextType {
  isPanelOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
}

interface JamesThreadContextType {
  activeThreadId: string | null;
  setActiveThreadId: (id: string | null) => void;
  activeThread: JamesThread | null;
  setActiveThread: (thread: JamesThread | null) => void;
}

export interface JamesStreamContextType {
  streamingState: StreamingState;
  streamingThreadId: string | null;
  sendStreamMessage: (threadId: string, content: string) => Promise<void>;
  quickStreamChat: (content: string) => Promise<string | null>;
  stopStream: () => void;
  resetStream: () => void;
}

// Combined type for backward compatibility
interface JamesContextType extends JamesPanelContextType, JamesThreadContextType {}

const JamesPanelContext = createContext<JamesPanelContextType | null>(null);
const JamesThreadContext = createContext<JamesThreadContextType | null>(null);
const JamesStreamContext = createContext<JamesStreamContextType | null>(null);

const initialStreamingState: StreamingState = {
  isStreaming: false,
  currentText: '',
  activeTools: new Map(),
  error: null,
  pendingUserMessage: null,
  clarifyingQuestion: null,
  wasInterrupted: false,
};

export function JamesProvider({ children }: { children: React.ReactNode }) {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [activeThread, setActiveThread] = useState<JamesThread | null>(null);

  // Streaming state — lives here so it survives page navigation
  const [streamingState, setStreamingState] = useState<StreamingState>(initialStreamingState);
  const [streamingThreadId, setStreamingThreadId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const abortControllerRef = useRef<AbortController | null>(null);

  const openPanel = useCallback(() => setIsPanelOpen(true), []);
  const closePanel = useCallback(() => setIsPanelOpen(false), []);
  const togglePanel = useCallback(() => setIsPanelOpen((prev) => !prev), []);

  const resetStream = useCallback(() => {
    setStreamingState(initialStreamingState);
    setStreamingThreadId(null);
  }, []);

  const stopStream = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setStreamingState((prev) => ({
      ...prev,
      isStreaming: false,
      wasInterrupted: true,
    }));
    queryClient.invalidateQueries({ queryKey: ['james', 'threads'] });
  }, [queryClient]);

  const handleStreamOptions = useCallback(
    (onThreadCreated?: (threadId: string) => void): StreamMessageOptions => ({
      onThreadCreated: (threadId) => {
        setStreamingThreadId(threadId);
        onThreadCreated?.(threadId);
      },
      onTextDelta: (text) => {
        setStreamingState((prev) => ({
          ...prev,
          currentText: prev.currentText + text,
        }));
      },
      onToolUseStart: (data: JamesToolUseStartEvent) => {
        setStreamingState((prev) => {
          const newTools = new Map(prev.activeTools);
          newTools.set(data.id, { name: data.name, status: 'running' });
          return { ...prev, activeTools: newTools };
        });
      },
      onToolUseResult: (data: JamesToolUseResultEvent) => {
        setStreamingState((prev) => {
          const newTools = new Map(prev.activeTools);
          const existingTool = newTools.get(data.id);
          if (existingTool) {
            const hasError = data.result && typeof data.result === 'object' && 'error' in data.result;
            newTools.set(data.id, {
              ...existingTool,
              status: hasError ? 'error' : 'completed',
              result: data.result,
            });
          }
          return { ...prev, activeTools: newTools };
        });
      },
      onAskUser: (data: JamesAskUserEvent) => {
        setStreamingState((prev) => ({
          ...prev,
          isStreaming: false,
          clarifyingQuestion: {
            id: data.id,
            question: data.question,
            options: data.options,
          },
        }));
        queryClient.invalidateQueries({ queryKey: ['james', 'threads'] });
      },
      onMessageComplete: () => {
        setStreamingState((prev) => ({
          ...prev,
          isStreaming: false,
        }));
        queryClient.invalidateQueries({ queryKey: ['james', 'threads'] });
      },
      onError: (message) => {
        setStreamingState((prev) => ({
          ...prev,
          isStreaming: false,
          error: message,
        }));
      },
    }),
    [queryClient]
  );

  const sendStreamMessage = useCallback(
    async (threadId: string, content: string) => {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      setStreamingThreadId(threadId);

      setStreamingState({
        isStreaming: true,
        currentText: '',
        activeTools: new Map(),
        error: null,
        pendingUserMessage: content,
        clarifyingQuestion: null,
        wasInterrupted: false,
      });

      try {
        await sendMessage(threadId, content, handleStreamOptions(), controller.signal);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        const error = err instanceof Error ? err.message : 'Failed to send message';
        setStreamingState((prev) => ({
          ...prev,
          isStreaming: false,
          error,
        }));
      }
    },
    [handleStreamOptions]
  );

  const quickStreamChat = useCallback(
    async (content: string): Promise<string | null> => {
      let threadId: string | null = null;
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setStreamingState({
        isStreaming: true,
        currentText: '',
        activeTools: new Map(),
        error: null,
        pendingUserMessage: content,
        clarifyingQuestion: null,
        wasInterrupted: false,
      });

      try {
        await quickChat(content, handleStreamOptions((id) => {
          threadId = id;
        }), controller.signal);
        return threadId;
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return threadId;
        const error = err instanceof Error ? err.message : 'Failed to send message';
        setStreamingState((prev) => ({
          ...prev,
          isStreaming: false,
          error,
        }));
        return null;
      }
    },
    [handleStreamOptions]
  );

  const panelValue = useMemo(
    () => ({ isPanelOpen, openPanel, closePanel, togglePanel }),
    [isPanelOpen, openPanel, closePanel, togglePanel]
  );

  const threadValue = useMemo(
    () => ({ activeThreadId, setActiveThreadId, activeThread, setActiveThread }),
    [activeThreadId, activeThread]
  );

  const streamValue = useMemo(
    () => ({ streamingState, streamingThreadId, sendStreamMessage, quickStreamChat, stopStream, resetStream }),
    [streamingState, streamingThreadId, sendStreamMessage, quickStreamChat, stopStream, resetStream]
  );

  return (
    <JamesPanelContext.Provider value={panelValue}>
      <JamesThreadContext.Provider value={threadValue}>
        <JamesStreamContext.Provider value={streamValue}>
          {children}
        </JamesStreamContext.Provider>
      </JamesThreadContext.Provider>
    </JamesPanelContext.Provider>
  );
}

// Granular hooks for consumers that only need one slice
export function useJamesPanelContext(): JamesPanelContextType {
  const context = useContext(JamesPanelContext);
  if (!context) {
    throw new Error('useJamesPanelContext must be used within a JamesProvider');
  }
  return context;
}

export function useJamesThreadContext(): JamesThreadContextType {
  const context = useContext(JamesThreadContext);
  if (!context) {
    throw new Error('useJamesThreadContext must be used within a JamesProvider');
  }
  return context;
}

export function useJamesStreamContext(): JamesStreamContextType {
  const context = useContext(JamesStreamContext);
  if (!context) {
    throw new Error('useJamesStreamContext must be used within a JamesProvider');
  }
  return context;
}

// Combined hook — backward compatible, re-renders on either change
export function useJamesContext(): JamesContextType {
  const panel = useJamesPanelContext();
  const thread = useJamesThreadContext();
  return { ...panel, ...thread };
}
